import { SizeType } from '../../lib/defs/types';
import {
  SharpenerBlockID,
  SharpenerBlockProps,
  SharpenerBlockType,
  sharpenerBlockProps,
} from '../blocks/sharpener/SharpenerBlock';
import {
  BarrelBlockID,
  BarrelBlockProps,
  BarrelBlockType,
  barrelBlockProps,
} from '../blocks/barrel/BarrelBlock';
import {
  RainbowSoapBlockID,
  RainbowSoapBlockProps,
  RainbowSoapBlockType,
  rainbowSoapBlockProps,
} from '../blocks/rainbowSoap/RainbowSoapBlock';
import {
  BigEggBlockID,
  BigEggBlockProps,
  BigEggBlockType,
  bigEggBlockProps,
} from '../blocks/bigEgg/BigEggBlock';
import {
  PiggyBankBlockID,
  PiggyBankBlockProps,
  PiggyBankBlockType,
  piggyBankBlockProps,
} from '../blocks/piggyBank/PiggyBankBlock';
import {
  BalloonBoxBlockID,
  BalloonBoxBlockProps,
  BalloonBoxBlockType,
  balloonBoxBlockProps,
} from '../blocks/balloonBox/BalloonBoxBlock';
import {
  OctopusBlockID,
  OctopusBlockProps,
  OctopusBlockType,
  octopusBlockProps,
} from '../blocks/octopus/OctopusBlock';
import {
  FlowerFidgetBlockID,
  FlowerFidgetBlockProps,
  FlowerFidgetBlockType,
  flowerFidgetBlockProps,
} from '../blocks/flowerFidget/FlowerFidgetBlock';
import {
  GrassBlockID,
  GrassBlockProps,
  GrassBlockType,
  grassBlockProps,
} from '../blocks/grass/GrassBlock';
import {
  MagicBunniesBlockID,
  MagicBunniesBlockProps,
  MagicBunniesBlockType,
  magicBunniesBlockProps,
} from '../blocks/magicBunnies/MagicBunniesBlock';
import { ChameleonColor } from '../views/blocks/ChameleonBlockView';
import { SirenColor } from '../views/blocks/SirenBlockView';
import { SymbolId } from './symbol';
import {
  LaserBoxBlockID,
  LaserBoxBlockProps,
  LaserBoxBlockType,
  laserBoxBlockProps,
} from '../blocks/laserBox/LaserBoxBlock';

// supporting types
//-----------------------------------------------------------------------------
// in order of highest impact first
export const powerBlockTypes = ['cube', 'bomb', 'rocket'] as const;
export type PowerBlockType = (typeof powerBlockTypes)[number];

export const colorIds = [
  'all',
  'blue',
  'green',
  'purple',
  'red',
  'yellow',
] as const;
export type ColorId = (typeof colorIds)[number];
export type AxisId = 'horizontal' | 'vertical';
export type DirectionId = 'left' | 'right' | 'up' | 'down';
export type ModeId = 'hard' | 'soft';

// block id
//-----------------------------------------------------------------------------
export type BlockId =
  | 'android'
  | 'animal'
  | 'basicBlue'
  | 'basicGreen'
  | 'basicPurple'
  | 'basicRed'
  | 'basicYellow'
  | 'bomb'
  | 'cage'
  | 'candle'
  | 'chain'
  | 'chameleon'
  | 'coffin'
  | 'cubeBlue'
  | 'cubeGreen'
  | 'cubePurple'
  | 'cubeRed'
  | 'cubeYellow'
  | 'dog'
  | 'doorAll'
  | 'doorBlue'
  | 'doorGreen'
  | 'doorPurple'
  | 'doorRed'
  | 'doorYellow'
  | 'firebox'
  | 'fireboxLollipop'
  | 'fugu'
  | 'hatAll'
  | 'hatBlue'
  | 'hatGreen'
  | 'hatPurple'
  | 'hatRed'
  | 'hatYellow'
  | 'hazard'
  | 'kappa'
  | 'kettle'
  | 'lock'
  | 'miniFugu'
  | 'pantherLarge'
  | 'pantherSmall'
  | 'pipe'
  | 'randomSpawn'
  | 'rocketHorizontal'
  | 'rocketVertical'
  | 'siren'
  | 'skull'
  | 'slime'
  | 'spider'
  | 'statue'
  | 'thrower'
  | 'virus'
  | 'wall'
  | 'zombie'
  | 'ice'
  | 'thickIce'
  | 'fullIce'
  | 'halfIce'
  | 'worm'
  | 'cocoon'
  | 'butterfly'
  | 'curtainAll'
  | 'curtainBlue'
  | 'curtainGreen'
  | 'curtainPurple'
  | 'curtainRed'
  | 'curtainYellow'
  | 'shell'
  | 'pond'
  | 'candyBag'
  | 'candy'
  | 'sodaBlue'
  | 'sodaGreen'
  | 'sodaPurple'
  | 'sodaRed'
  | 'sodaYellow'
  | 'bubbles'
  | 'alarm'
  | 'chocolateEgg'
  | 'nest'
  | 'mushroom'
  | 'parcels'
  | 'fossil'
  | 'corn'
  | 'popcorn'
  | 'tunaCan'
  | 'tunaFish'
  | 'rainbowBubble'
  | 'firecrackerBox'
  | 'cooler'
  | 'tvAll'
  | 'tvBlue'
  | 'tvGreen'
  | 'tvPurple'
  | 'tvRed'
  | 'tvYellow'
  | 'shieldBubble'
  | 'bathtub'
  | SharpenerBlockID
  | BarrelBlockID
  | RainbowSoapBlockID
  | BigEggBlockID
  | PiggyBankBlockID
  | BalloonBoxBlockID
  | OctopusBlockID
  | FlowerFidgetBlockID
  | GrassBlockID
  | MagicBunniesBlockID
  | LaserBoxBlockID;

export type BlockType =
  | 'android'
  | 'animal'
  | 'basic'
  | 'bomb'
  | 'cage'
  | 'candle'
  | 'chain'
  | 'chameleon'
  | 'coffin'
  | 'cube'
  | 'dog'
  | 'door'
  | 'firebox'
  | 'fugu'
  | 'hat'
  | 'hazard'
  | 'kappa'
  | 'kettle'
  | 'lock'
  | 'miniFugu'
  | 'panther'
  | 'pipe'
  | 'randomSpawn'
  | 'rocket'
  | 'siren'
  | 'skull'
  | 'slime'
  | 'spider'
  | 'statue'
  | 'thrower'
  | 'virus'
  | 'wall'
  | 'zombie'
  | 'ice'
  | 'thickIce'
  | 'fullIce'
  | 'halfIce'
  | 'worm'
  | 'cocoon'
  | 'butterfly'
  | 'curtain'
  | 'shell'
  | 'pond'
  | 'candyBag'
  | 'candy'
  | 'soda'
  | 'bubbles'
  | 'alarm'
  | 'chocolateEgg'
  | 'nest'
  | 'mushroom'
  | 'wall'
  | 'parcels'
  | 'fossil'
  | 'corn'
  | 'popcorn'
  | 'tunaCan'
  | 'tunaFish'
  | 'rainbowBubble'
  | 'firecrackerBox'
  | 'cooler'
  | 'tv'
  | 'shieldBubble'
  | 'bathtub'
  | SharpenerBlockType
  | BarrelBlockType
  | RainbowSoapBlockType
  | BigEggBlockType
  | PiggyBankBlockType
  | BalloonBoxBlockType
  | OctopusBlockType
  | FlowerFidgetBlockType
  | GrassBlockType
  | MagicBunniesBlockType
  | LaserBoxBlockType;

// block options
//-----------------------------------------------------------------------------
//TODO: refactor options
export type CountBlockOptionDef = {
  count: number;
  spawnBlocks: BlockId[];
};

export type ColorBlockOptionDef = {
  color: ChameleonColor;
};

export type ColorsBlockOptionDef = {
  colors: SirenColor[];
};

export type DirectionBlockOptionDef = {
  direction: DirectionId;
};

export type ModeBlockOptionDef = {
  mode: ModeId;
};

export type BlockOptionDef =
  | {}
  | CountBlockOptionDef
  | ColorsBlockOptionDef
  | DirectionBlockOptionDef
  | ModeBlockOptionDef;

export type BlockOptionType =
  | 'count'
  | 'countSize'
  | 'list'
  | 'countDirection'
  | 'countColor';

// block types
//-----------------------------------------------------------------------------
export type BaseBlockProps = {
  // short unique id used for encoding
  // WARNING: do NOT change once in production
  id: string;
  // if block can fall or not
  immobile?: boolean;
  // if block uses overlay cell layer instead of base layer
  overlay?: boolean;
  // if overlay should not block tap interaction, like cages
  tappable?: boolean;
  //TODO: remove need for this. its a hack used to make combo glowy look right
  // this block is higher z order than neighbor blocks
  overlap?: boolean;
  // for overlay blocks. this block freezes the block below such
  // that it cannot be affected by other blocks, effects, or collides.
  freeze?: boolean;
  // for overlay blocks. this block binds to the block below such
  // that it moves with it and is not subjected to the shuffle action.
  bind?: boolean;
  // for overlay blocks. prevents blocks from falling under this block.
  barrier?: boolean;
  // will (should) not be destroyed.
  immortal?: boolean;
  //TODO: refactor options/type
  // used by editor as a list of option choices for the block
  options?: BlockOptionDef[];
  // options type
  optionType?: BlockOptionType;
  // whether to allow this block to be created in editor view
  noEditor?: boolean;
  // whether to allow this block to have multiple options in editor view
  noEditorOptions?: boolean;
  // some blocks might have custom/shared goals that are not exactly themselves, used for blocks
  // that have multiple stages (with different block ids) and only the last stage is the actual goal,
  // like ThickIce and FullIce, they both should have HalfIce as their goal, as it is their last stage.
  customGoal?: BlockId;
  // some blocks should share a common goal count, it's different from customGoal because they are not
  // one of the stages of the block itself, but actually another block id of the same "family"
  // like Soda Bottles: sodaRed, sodaBlue, sodaGreen, sodaPurple, sodaYellow all share the same sodaRed goal count.
  // This actually changes the goal trigger of the block: despawned blocks will trigger the shared goal instead
  sharedGoal?: BlockId;
  // generator type specifies that the block can produce other blocks or unlimited number of goals
  // essentially allowing number of goals on editor to exceed the number of obstacles placed on the map
  generator?: boolean;
  // some blocks might result in more than a single goal count, like
  // CandyBag where it will break into 4 Candy blocks which is the end goal.
  goalCountMultiplier?: number;
  // if this block can be collided with multiple times under the same collision tracker (cycle)
  multiCollidable?: boolean;
  // A left-to-right block with width more than 1 should be anchored on the right side.
  orientation?: 'leftToRight' | 'rightToLeft' | 'bottomToTop' | 'topToBottom';
} & SizeType;

export type AndroidBlockProps = {
  type: 'android';
} & BaseBlockProps;

export type AnimalBlockProps = {
  type: 'animal';
} & BaseBlockProps;

export type BombBlockProps = {
  type: 'bomb';
  radius: number;
} & BaseBlockProps;

export type BasicBlockProps = {
  type: 'basic';
  color: ColorId;
  symbol: SymbolId;
} & BaseBlockProps;

export type CageBlockProps = {
  type: 'cage';
} & BaseBlockProps;

export type CandleBlockProps = {
  type: 'candle';
} & BaseBlockProps;

export type ChainBlockProps = {
  type: 'chain';
} & BaseBlockProps;

export type ChameleonBlockProps = {
  type: 'chameleon';
} & BaseBlockProps;

export type CoffinBlockProps = {
  type: 'coffin';
} & BaseBlockProps;

export type CubeBlockProps = {
  type: 'cube';
  color: ColorId;
} & BaseBlockProps;

export type DogBlockProps = {
  type: 'dog';
} & BaseBlockProps;

export type DoorBlockProps = {
  type: 'door';
  color: ColorId;
} & BaseBlockProps;

export type FireboxBlockProps = {
  type: 'firebox';
} & BaseBlockProps;

export type FuguBlockProps = {
  type: 'fugu';
} & BaseBlockProps;

export type HatBlockProps = {
  type: 'hat';
  color: ColorId;
} & BaseBlockProps;

export type HazardBlockProps = {
  type: 'hazard';
} & BaseBlockProps;

export type KettleBlockProps = {
  type: 'kettle';
} & BaseBlockProps;

export type LockBlockProps = {
  type: 'lock';
} & BaseBlockProps;

export type MiniFuguBlockProps = {
  type: 'miniFugu';
} & BaseBlockProps;

export type PantherBlockProps = {
  type: 'panther';
} & BaseBlockProps;

export type PipeBlockProps = {
  type: 'pipe';
} & BaseBlockProps;

export type RandomSpawnBlockProps = {
  type: 'randomSpawn';
} & BaseBlockProps;

export type RocketBlockProps = {
  type: 'rocket';
} & BaseBlockProps;

export type SirenBlockProps = {
  type: 'siren';
} & BaseBlockProps;

export type SkullBlockProps = {
  type: 'skull';
} & BaseBlockProps;

export type SlimeBlockProps = {
  type: 'slime';
} & BaseBlockProps;

export type SpiderBlockProps = {
  type: 'spider';
} & BaseBlockProps;

export type KappaBlockProps = {
  type: 'kappa';
} & BaseBlockProps;

export type CurtainBlockProps = {
  type: 'curtain';
  color: ColorId;
  width: number;
  height: number;
  count: number;
} & BaseBlockProps;

export type StatueBlockProps = {
  type: 'statue';
} & BaseBlockProps;

export type ThrowerBlockProps = {
  type: 'thrower';
} & BaseBlockProps;

export type VirusBlockProps = {
  type: 'virus';
} & BaseBlockProps;

export type WallBlockProps = {
  type: 'wall';
  width: number;
  height: number;
  count: number;
} & BaseBlockProps;

export type ZombieBlockProps = {
  type: 'zombie';
} & BaseBlockProps;

export type IceBlockProps = {
  type: 'ice';
} & BaseBlockProps;

export type ThickIceBlockProps = {
  type: 'thickIce';
} & BaseBlockProps;

export type FullIceBlockProps = {
  type: 'fullIce';
} & BaseBlockProps;

export type HalfIceBlockProps = {
  type: 'halfIce';
} & BaseBlockProps;

export type WormBlockProps = {
  type: 'worm';
} & BaseBlockProps;

export type CocoonBlockProps = {
  type: 'cocoon';
} & BaseBlockProps;

export type ButterflyBlockProps = {
  type: 'butterfly';
} & BaseBlockProps;

export type PondBlockProps = {
  type: 'pond';
} & BaseBlockProps;

export type CandyBagBlockProps = {
  type: 'candyBag';
} & BaseBlockProps;

export type CandyBlockProps = {
  type: 'candy';
} & BaseBlockProps;

export type ShellBlockProps = {
  type: 'shell';
} & BaseBlockProps;

export type SodaBlockProps = {
  type: 'soda';
  color: ColorId;
  direction: DirectionId;
  count: number;
} & BaseBlockProps;

export type BubblesBlockProps = {
  type: 'bubbles';
  amount: number;
} & BaseBlockProps;

export type AlarmBlockProps = {
  type: 'alarm';
} & BaseBlockProps;

export type ChocolateEggBlockProps = {
  type: 'chocolateEgg';
} & BaseBlockProps;

export type NestBlockProps = {
  type: 'nest';
} & BaseBlockProps;

export type MushroomBlockProps = {
  type: 'mushroom';
} & BaseBlockProps;

export type ParcelsBlockProps = {
  type: 'parcels';
} & BaseBlockProps;

export type FossilBlockProps = {
  type: 'fossil';
} & BaseBlockProps;

export type CornBlockProps = {
  type: 'corn';
} & BaseBlockProps;

export type PopcornBlockProps = {
  type: 'popcorn';
} & BaseBlockProps;

export type TunaCanBlockProps = {
  type: 'tunaCan';
} & BaseBlockProps;

export type TunaFishBlockProps = {
  type: 'tunaFish';
} & BaseBlockProps;

export type RainbowBubbleBlockProps = {
  type: 'rainbowBubble';
} & BaseBlockProps;

export type FirecrackerBoxBlockProps = {
  type: 'firecrackerBox';
  count: number;
  maxCount: number;
  color: ColorId;
} & BaseBlockProps;

export type CoolerBlockProps = {
  type: 'cooler';
} & BaseBlockProps;

export type TvBlockProps = {
  type: 'tv';
  color: ColorId;
} & BaseBlockProps;

export type ShieldBubbleBlockProps = {
  type: 'shieldBubble';
} & BaseBlockProps;

export type BathtubBlockProps = {
  type: 'bathtub';
} & BaseBlockProps;

export type BlockProps =
  | AndroidBlockProps
  | AnimalBlockProps
  | BombBlockProps
  | BasicBlockProps
  | CageBlockProps
  | CandleBlockProps
  | ChainBlockProps
  | ChameleonBlockProps
  | CoffinBlockProps
  | CubeBlockProps
  | DogBlockProps
  | DoorBlockProps
  | FireboxBlockProps
  | HatBlockProps
  | HazardBlockProps
  | KettleBlockProps
  | LockBlockProps
  | MiniFuguBlockProps
  | PantherBlockProps
  | PipeBlockProps
  | RandomSpawnBlockProps
  | RocketBlockProps
  | SirenBlockProps
  | SkullBlockProps
  | SlimeBlockProps
  | SpiderBlockProps
  | FuguBlockProps
  | KappaBlockProps
  | StatueBlockProps
  | ThrowerBlockProps
  | VirusBlockProps
  | WallBlockProps
  | ZombieBlockProps
  | IceBlockProps
  | ThickIceBlockProps
  | FullIceBlockProps
  | HalfIceBlockProps
  | WormBlockProps
  | CocoonBlockProps
  | ButterflyBlockProps
  | CurtainBlockProps
  | PondBlockProps
  | CandyBagBlockProps
  | CandyBlockProps
  | ShellBlockProps
  | SodaBlockProps
  | BubblesBlockProps
  | AlarmBlockProps
  | ChocolateEggBlockProps
  | NestBlockProps
  | MushroomBlockProps
  | ParcelsBlockProps
  | FossilBlockProps
  | CornBlockProps
  | PopcornBlockProps
  | TunaCanBlockProps
  | TunaFishBlockProps
  | RainbowBubbleBlockProps
  | FirecrackerBoxBlockProps
  | CoolerBlockProps
  | TvBlockProps
  | ShieldBubbleBlockProps
  | BathtubBlockProps
  | SharpenerBlockProps
  | BarrelBlockProps
  | RainbowSoapBlockProps
  | BigEggBlockProps
  | PiggyBankBlockProps
  | BalloonBoxBlockProps
  | OctopusBlockProps
  | FlowerFidgetBlockProps
  | GrassBlockProps
  | MagicBunniesBlockProps
  | LaserBoxBlockProps;

// tables
//-----------------------------------------------------------------------------
const doorOptions: BlockOptionDef[] = [
  { count: 0 },
  { count: 1 },
  { count: 2 },
];

export const blockPropsMap: Record<BlockId, BlockProps> = {
  // random spawn
  // This is a special type of block that is only a placeholder for spawning a random block.
  randomSpawn: {
    id: 'rs',
    type: 'randomSpawn',
    immobile: true,
    immortal: true,
  },

  // android
  android: {
    id: 'A',
    type: 'android',
    width: 2,
    height: 2,
    immobile: true,
    options: [{ count: 2 }, { count: 1 }, { count: 0 }],
    optionType: 'list',
  },

  // animal
  animal: {
    id: 'a',
    type: 'animal',
    immobile: true,
  },

  // basic
  basicBlue: {
    id: 'bb',
    type: 'basic',
    color: 'blue',
    symbol: 'spade',
  },
  basicGreen: {
    id: 'bg',
    type: 'basic',
    color: 'green',
    symbol: 'club',
  },
  basicPurple: {
    id: 'bp',
    type: 'basic',
    color: 'purple',
    symbol: 'joker',
  },
  basicRed: {
    id: 'br',
    type: 'basic',
    color: 'red',
    symbol: 'heart',
  },
  basicYellow: {
    id: 'by',
    type: 'basic',
    color: 'yellow',
    symbol: 'diamond',
  },

  // bomb
  bomb: {
    id: 'B',
    type: 'bomb',
    radius: 1,
    overlap: true,
  },

  // cage
  cage: {
    id: 'c',
    type: 'cage',
    overlay: true,
    immobile: true,
    tappable: true,
  },

  // candle
  candle: {
    id: 'C',
    type: 'candle',
    width: 2,
    height: 2,
    immobile: true,
    options: [
      { count: 7 },
      { count: 1 },
      { count: 2 },
      { count: 3 },
      { count: 4 },
      { count: 5 },
      { count: 6 },
      { count: 8 },
      { count: 9 },
    ],
    optionType: 'list',
    multiCollidable: true,
  },

  // chain
  chain: {
    id: 'ch',
    type: 'chain',
    immobile: true,
    options: [{ count: 0 }, { count: 1 }, { count: 2 }],
  },

  // chameleon
  chameleon: {
    id: 'ca',
    type: 'chameleon',
    options: [
      { color: 'blue' },
      { color: 'red' },
      { color: 'yellow' },
      { color: 'green' },
      { color: 'purple' },
    ],
    optionType: 'list',
  },

  coffin: {
    id: 'cf',
    type: 'coffin',
    immobile: true,
    customGoal: 'zombie',
  },

  // coffins turn into zombies
  zombie: {
    id: 'zo',
    type: 'zombie',
  },

  // cube
  cubeBlue: {
    id: 'ub',
    type: 'cube',
    color: 'blue',
    overlap: true,
  },
  cubeGreen: {
    id: 'ug',
    type: 'cube',
    color: 'green',
    overlap: true,
  },
  cubePurple: {
    id: 'up',
    type: 'cube',
    color: 'purple',
    overlap: true,
  },
  cubeRed: {
    id: 'ur',
    type: 'cube',
    color: 'red',
    overlap: true,
  },
  cubeYellow: {
    id: 'uy',
    type: 'cube',
    color: 'yellow',
    overlap: true,
  },

  // dog
  dog: {
    id: 'd',
    type: 'dog',
    immobile: true,
    options: [{ direction: 'right' }, { direction: 'left' }],
    optionType: 'list',
  },

  // door
  doorAll: {
    id: 'da',
    type: 'door',
    color: 'all',
    immobile: true,
    options: doorOptions,
    optionType: 'list',
  },
  doorBlue: {
    id: 'db',
    type: 'door',
    color: 'blue',
    immobile: true,
    options: doorOptions,
    optionType: 'list',
  },
  doorGreen: {
    id: 'dg',
    type: 'door',
    color: 'green',
    immobile: true,
    options: doorOptions,
    optionType: 'list',
  },
  doorPurple: {
    id: 'dp',
    type: 'door',
    color: 'purple',
    immobile: true,
    options: doorOptions,
    optionType: 'list',
  },
  doorRed: {
    id: 'dr',
    type: 'door',
    color: 'red',
    immobile: true,
    options: doorOptions,
    optionType: 'list',
  },
  doorYellow: {
    id: 'dy',
    type: 'door',
    color: 'yellow',
    immobile: true,
    options: doorOptions,
    optionType: 'list',
  },

  // firebox
  firebox: {
    id: 'fb',
    type: 'firebox',
    options: [
      // 50% bomb, 50% rocket
      {
        count: 2,
        spawnBlocks: ['bomb', 'bomb', 'rocketHorizontal', 'rocketVertical'],
      },
      {
        count: 1,
        spawnBlocks: ['bomb', 'bomb', 'rocketHorizontal', 'rocketVertical'],
      },
      {
        count: 0,
        spawnBlocks: ['bomb', 'bomb', 'rocketHorizontal', 'rocketVertical'],
      },
    ],
    optionType: 'list',
  },

  fireboxLollipop: {
    id: 'fbl',
    type: 'firebox',
    options: [
      // Lollipop
      {
        count: 2,
        spawnBlocks: [
          'cubeBlue',
          'cubeGreen',
          'cubePurple',
          'cubeRed',
          'cubeYellow',
        ],
      },
      {
        count: 1,
        spawnBlocks: [
          'cubeBlue',
          'cubeGreen',
          'cubePurple',
          'cubeRed',
          'cubeYellow',
        ],
      },
      {
        count: 0,
        spawnBlocks: [
          'cubeBlue',
          'cubeGreen',
          'cubePurple',
          'cubeRed',
          'cubeYellow',
        ],
      },
    ],
    optionType: 'list',
  },

  // fugu
  fugu: {
    id: 'fu',
    type: 'fugu',
    customGoal: 'miniFugu',
  },
  miniFugu: {
    id: 'mfu',
    type: 'miniFugu',
  },

  // hat
  hatAll: {
    id: 'ha',
    type: 'hat',
    color: 'all',
  },
  hatBlue: {
    id: 'hb',
    type: 'hat',
    color: 'blue',
  },
  hatGreen: {
    id: 'hg',
    type: 'hat',
    color: 'green',
  },
  hatPurple: {
    id: 'hp',
    type: 'hat',
    color: 'purple',
  },
  hatRed: {
    id: 'hr',
    type: 'hat',
    color: 'red',
  },
  hatYellow: {
    id: 'hy',
    type: 'hat',
    color: 'yellow',
  },

  // hazard
  hazard: {
    id: 'hz',
    type: 'hazard',
    immobile: true,
    customGoal: 'virus',
  },
  virus: {
    id: 'vi',
    type: 'virus',
  },

  // kettle
  kettle: {
    id: 'kt',
    type: 'kettle',
  },

  // lock
  lock: {
    id: 'l',
    type: 'lock',
    options: [{ count: 1 }, { count: 0 }],
    optionType: 'list',
  },

  // panther
  pantherLarge: {
    id: 'pl',
    type: 'panther',
    width: 2,
    height: 2,
  },
  pantherSmall: {
    id: 'ps',
    type: 'panther',
  },

  // pipe
  pipe: {
    id: 'P',
    type: 'pipe',
    immobile: true,
    immortal: true,
    generator: true,
  },

  // rocket
  rocketHorizontal: {
    id: 'rh',
    type: 'rocket',
    overlap: true,
  },
  rocketVertical: {
    id: 'rv',
    type: 'rocket',
    overlap: true,
  },

  // siren
  siren: {
    id: 's',
    type: 'siren',
    width: 2,
    height: 2,
    immobile: true,
    options: [
      { colors: ['blue', 'green', 'red', 'yellow'] },
      { colors: ['blue', 'blue', 'blue', 'blue'] },
      { colors: ['green', 'green', 'green', 'green'] },
      { colors: ['red', 'red', 'red', 'red'] },
      { colors: ['yellow', 'yellow', 'yellow', 'yellow'] },
    ],
    optionType: 'list',
  },

  // skull
  skull: {
    id: 'S',
    type: 'skull',
    options: [{ count: 1 }, { count: 0 }],
    optionType: 'list',
  },

  // slime
  slime: {
    id: 'sl',
    type: 'slime',
    overlay: true,
    immobile: true,
    freeze: true,
  },

  // kappa
  kappa: {
    id: 'ka',
    type: 'kappa',
    options: [{ mode: 'hard' }, { mode: 'soft' }],
  },

  // spider
  spider: {
    id: 'sp',
    type: 'spider',
  },

  // statue
  statue: {
    id: 'st',
    type: 'statue',
    options: [{ count: 2 }, { count: 1 }, { count: 0 }],
    optionType: 'list',
  },

  // thrower
  thrower: {
    id: 't',
    type: 'thrower',
    immobile: true,
    immortal: true,
    generator: true,
    customGoal: 'cage',
  },

  // ice blocks
  thickIce: {
    id: 'ti',
    type: 'thickIce',
    overlay: true,
    freeze: true,
    customGoal: 'halfIce',
  },
  fullIce: {
    id: 'fi',
    type: 'fullIce',
    overlay: true,
    freeze: true,
    customGoal: 'halfIce',
  },
  halfIce: {
    id: 'hi',
    type: 'halfIce',
    overlay: true,
    bind: true,
  },

  // Standalone ice, does not break into halfIce
  ice: {
    id: 'i',
    type: 'ice',
    overlay: true,
    freeze: true,
  },

  // worm -> cocoon -> butterfly
  worm: {
    id: 'w',
    type: 'worm',
    customGoal: 'butterfly',
  },
  cocoon: {
    id: 'cc',
    type: 'cocoon',
    immobile: true,
    noEditor: true,
    customGoal: 'butterfly',
  },
  butterfly: {
    id: 'bf',
    type: 'butterfly',
    noEditor: true,
  },

  // curtain
  curtainAll: {
    id: 'cta',
    color: 'all',
    type: 'curtain',
    optionType: 'countSize',
    overlay: true,
    freeze: true,
    width: 3,
    height: 3,
    count: 20,
    // Min/max size for editing purposes
    minWidth: 2,
    minHeight: 2,
    maxWidth: 9,
    maxHeight: 9,
  },
  curtainBlue: {
    id: 'ctb',
    color: 'blue',
    type: 'curtain',
    optionType: 'countSize',
    overlay: true,
    freeze: true,
    width: 3,
    height: 3,
    count: 20,
    // Min/max size for editing purposes
    minWidth: 2,
    minHeight: 2,
    maxWidth: 9,
    maxHeight: 9,
  },
  curtainGreen: {
    id: 'ctg',
    color: 'green',
    type: 'curtain',
    optionType: 'countSize',
    overlay: true,
    freeze: true,
    width: 3,
    height: 3,
    count: 20,
    // Min/max size for editing purposes
    minWidth: 2,
    minHeight: 2,
    maxWidth: 9,
    maxHeight: 9,
  },
  curtainPurple: {
    id: 'ctp',
    color: 'purple',
    type: 'curtain',
    optionType: 'countSize',
    overlay: true,
    freeze: true,
    width: 3,
    height: 3,
    count: 20,
    // Min/max size for editing purposes
    minWidth: 2,
    minHeight: 2,
    maxWidth: 9,
    maxHeight: 9,
  },
  curtainRed: {
    id: 'ctr',
    color: 'red',
    type: 'curtain',
    optionType: 'countSize',
    overlay: true,
    freeze: true,
    width: 3,
    height: 3,
    count: 20,
    // Min/max size for editing purposes
    minWidth: 2,
    minHeight: 2,
    maxWidth: 9,
    maxHeight: 9,
  },
  curtainYellow: {
    id: 'cty',
    color: 'yellow',
    type: 'curtain',
    optionType: 'countSize',
    overlay: true,
    freeze: true,
    width: 3,
    height: 3,
    count: 20,
    // Min/max size for editing purposes
    minWidth: 2,
    minHeight: 2,
    maxWidth: 9,
    maxHeight: 9,
  },

  // Pond
  pond: {
    id: 'dv',
    type: 'pond',
    immobile: true,
    options: [{ count: 0 }, { count: 1 }],
    noEditorOptions: true,
  },

  // candy bag
  candyBag: {
    id: 'cb',
    type: 'candyBag',
    width: 2,
    height: 2,
    customGoal: 'candy',
    goalCountMultiplier: 4,
  },
  candy: {
    id: 'cd',
    type: 'candy',
    immobile: true,
    noEditor: true,
  },

  // shell
  shell: {
    id: 'sh',
    type: 'shell',
    options: [false, true],
  },

  // soda
  sodaBlue: {
    id: 'sb',
    type: 'soda',
    immobile: true,
    color: 'blue',
    count: 4,
    direction: 'up',
    options: [
      { direction: 'up', height: 2 },
      { direction: 'right', width: 2 },
      { direction: 'down', height: 2 },
      { direction: 'left', width: 2 },
    ],
    optionType: 'countDirection',
    sharedGoal: 'sodaRed', // All soda goals are represented by sodaRed
  },
  sodaGreen: {
    id: 'sg',
    type: 'soda',
    immobile: true,
    color: 'green',
    count: 4,
    direction: 'up',
    options: [
      { direction: 'up', height: 2 },
      { direction: 'right', width: 2 },
      { direction: 'down', height: 2 },
      { direction: 'left', width: 2 },
    ],
    optionType: 'countDirection',
    sharedGoal: 'sodaRed', // All soda goals are represented by sodaRed
  },
  sodaPurple: {
    id: 'spu',
    type: 'soda',
    immobile: true,
    color: 'purple',
    count: 4,
    direction: 'up',
    options: [
      { direction: 'up', height: 2 },
      { direction: 'right', width: 2 },
      { direction: 'down', height: 2 },
      { direction: 'left', width: 2 },
    ],
    optionType: 'countDirection',
    sharedGoal: 'sodaRed', // All soda goals are represented by sodaRed
  },
  sodaRed: {
    id: 'sr',
    type: 'soda',
    immobile: true,
    color: 'red',
    count: 4,
    direction: 'up',
    options: [
      { direction: 'up', height: 2 },
      { direction: 'right', width: 2 },
      { direction: 'down', height: 2 },
      { direction: 'left', width: 2 },
    ],
    optionType: 'countDirection',
  },
  sodaYellow: {
    id: 'sy',
    type: 'soda',
    immobile: true,
    color: 'yellow',
    count: 4,
    direction: 'up',
    options: [
      { direction: 'up', height: 2 },
      { direction: 'right', width: 2 },
      { direction: 'down', height: 2 },
      { direction: 'left', width: 2 },
    ],
    optionType: 'countDirection',
    sharedGoal: 'sodaRed', // All soda goals are represented by sodaRed
  },

  // bubbles
  bubbles: {
    id: 'bu',
    type: 'bubbles',
    amount: 10,
  },

  // alarm
  alarm: {
    id: 'al',
    type: 'alarm',
    immobile: true,
    options: [{ count: 1 }, { count: 0 }],
    optionType: 'list',
  },

  // chocolate egg
  chocolateEgg: {
    id: 'ce',
    type: 'chocolateEgg',
    options: [{ count: 3 }, { count: 2 }, { count: 1 }, { count: 0 }],
    optionType: 'list',
  },

  // nest
  nest: {
    id: 'n',
    type: 'nest',
    immobile: true,
    generator: true,
  },

  // mushroom
  mushroom: {
    id: 'm',
    type: 'mushroom',
    immobile: true,
  },

  // Wall (clouds)
  wall: {
    id: 'wa',
    type: 'wall',
    overlay: true,
    freeze: true,
    width: 3,
    height: 4,
    count: 20,
  },

  parcels: {
    id: 'pcs',
    type: 'parcels',
    immobile: true,
    immortal: true,
    generator: true,
    customGoal: 'hatAll',
  },

  // Fossil stages: full rock, cracked rock, fossil revealed, fossil only
  fossil: {
    id: 'fo',
    type: 'fossil',
    immobile: true,
    options: [{ count: 3 }, { count: 2 }, { count: 1 }, { count: 0 }],
    optionType: 'list',
  },

  // Corn -> Popcorn
  corn: {
    id: 'co',
    type: 'corn',
    immobile: true,
    customGoal: 'popcorn',
  },
  popcorn: {
    id: 'po',
    type: 'popcorn',
  },

  // Tuna Can -> Tuna Fish
  tunaCan: {
    id: 'tc',
    type: 'tunaCan',
    customGoal: 'tunaFish',
  },
  tunaFish: {
    id: 'tf',
    type: 'tunaFish',
  },

  // Rainbow Bubble
  rainbowBubble: {
    id: 'rb',
    type: 'rainbowBubble',
    overlay: true,
    immobile: true,
    bind: true,
  },

  // Firecracker Box
  firecrackerBox: {
    id: 'fc',
    type: 'firecrackerBox',
    optionType: 'countColor',
    width: 3,
    height: 3,
    immobile: true,
    color: 'red',
    count: 7,
    maxCount: 7,
  },

  // Soda cooler block with 2 stages: closed and open
  cooler: {
    id: 'cl',
    type: 'cooler',
    options: [{ count: 1 }, { count: 0 }],
    optionType: 'list',
  },

  // tv
  tvAll: {
    id: 'tva',
    type: 'tv',
    color: 'all',
  },
  tvBlue: {
    id: 'tvb',
    type: 'tv',
    color: 'blue',
    customGoal: 'tvAll',
  },
  tvGreen: {
    id: 'tvg',
    type: 'tv',
    color: 'green',
    customGoal: 'tvAll',
  },
  tvPurple: {
    id: 'tvp',
    type: 'tv',
    color: 'purple',
    customGoal: 'tvAll',
  },
  tvRed: {
    id: 'tvr',
    type: 'tv',
    color: 'red',
    customGoal: 'tvAll',
  },
  tvYellow: {
    id: 'tvy',
    type: 'tv',
    color: 'yellow',
    customGoal: 'tvAll',
  },

  // Shield Bubble
  shieldBubble: {
    id: 'shb',
    type: 'shieldBubble',
    overlay: true,
    immobile: true,
    bind: true,
  },

  // Bathtub that throw bubbles when destroyed
  bathtub: {
    id: 'bt',
    type: 'bathtub',
    width: 2,
    height: 2,
    immobile: true,
    options: [
      { count: 4 },
      { count: 3 },
      { count: 2 },
      { count: 1 },
      { count: 0 },
    ],
    optionType: 'list',
  },

  ...sharpenerBlockProps,
  ...barrelBlockProps,
  ...rainbowSoapBlockProps,
  ...bigEggBlockProps,
  ...piggyBankBlockProps,
  ...balloonBoxBlockProps,
  ...octopusBlockProps,
  ...flowerFidgetBlockProps,
  ...grassBlockProps,
  ...magicBunniesBlockProps,
  ...laserBoxBlockProps,
};

export const colorToBlockId: {
  [key in ColorId]: { [key in BlockType]?: BlockId };
} = {
  all: {},
  blue: {
    basic: 'basicBlue',
    cube: 'cubeBlue',
  },
  green: {
    basic: 'basicGreen',
    cube: 'cubeGreen',
  },
  purple: {
    basic: 'basicPurple',
    cube: 'cubePurple',
  },
  red: {
    basic: 'basicRed',
    cube: 'cubeRed',
  },
  yellow: {
    basic: 'basicYellow',
    cube: 'cubeYellow',
  },
};

export const blockColorMap: {
  [key in ColorId]: { base: number; glow: number };
} = {
  all: {
    base: 0xffffff,
    glow: 0xffffff,
  },
  blue: {
    base: 0x23adea,
    glow: 0x00ccff,
  },
  green: {
    base: 0x59cf00,
    glow: 0x00ff88,
  },
  purple: {
    base: 0x9941e2,
    glow: 0xbc70f5,
  },
  red: {
    base: 0xef114e,
    glow: 0xff0088,
  },
  yellow: {
    base: 0xf4d100,
    glow: 0xffcc00,
  },
};
